import { enqueueSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import {
  Button,
  FormControlLabel,
  Switch,
  Grid,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function WorkflowDeliverySchedule({ workflow, api, onChanged }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      sendFirstEmailOnEnrollment: Boolean(workflow.sendFirstEmailOnEnrollment),
      sendTimezone: workflow.sendTimezone,
      sendWindowStart: workflow.sendWindowStart,
      sendWindowEnd: workflow.sendWindowEnd,
      activeWeekdays: workflow.activeWeekdays || [],
    });
  }, [workflow]);

  const save = async () => {
    if (!form.activeWeekdays.length) return;
    setSaving(true);
    try {
      const result = await api.updateWorkflow(workflow.id, {
        name: workflow.name,
        description: workflow.description,
        isEnabled: workflow.isEnabled,
        ...form,
      });
      if (!result.success) {
        enqueueSnackbar(result.errors.map((item) => item.message).join(" "), {
          variant: "error",
        });
        return;
      }
      enqueueSnackbar("Delivery schedule saved.", { variant: "success" });
      await onChanged();
    } catch (error) {
      enqueueSnackbar(error.message, { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={2}>
        <BoxHeader />
        <FormControlLabel
          label="Send the first email immediately on enrollment"
          control={
            <Switch
              checked={Boolean(form.sendFirstEmailOnEnrollment)}
              disabled={saving}
              onChange={(_, checked) =>
                setForm({ ...form, sendFirstEmailOnEnrollment: checked })
              }
            />
          }
        />
        <Typography variant="body2" color="text.secondary">
          When enabled, the entry stage’s first enabled email with zero delay is
          queued on new enrollment, even outside the selected hours and days.
          Later emails follow the normal schedule. Sender limits and eligibility
          checks still apply.
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Timezone"
              value={form.sendTimezone || ""}
              onChange={(event) =>
                setForm({ ...form, sendTimezone: event.target.value })
              }
            />
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <TextField
              fullWidth
              type="time"
              label="Send from"
              InputLabelProps={{ shrink: true }}
              value={(form.sendWindowStart || "09:00").slice(0, 5)}
              onChange={(event) =>
                setForm({ ...form, sendWindowStart: event.target.value })
              }
            />
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <TextField
              fullWidth
              type="time"
              label="Send until"
              InputLabelProps={{ shrink: true }}
              value={(form.sendWindowEnd || "17:00").slice(0, 5)}
              onChange={(event) =>
                setForm({ ...form, sendWindowEnd: event.target.value })
              }
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Button
              variant="contained"
              onClick={save}
              disabled={saving || !form.activeWeekdays?.length}
            >
              Save schedule
            </Button>
          </Grid>
          <Grid size={12}>
            <ToggleButtonGroup
              value={form.activeWeekdays || []}
              onChange={(_, activeWeekdays) =>
                setForm({ ...form, activeWeekdays })
              }
              aria-label="Active workflow delivery days"
              size="small"
              sx={{ flexWrap: "wrap" }}
            >
              {WEEKDAYS.map((day, index) => (
                <ToggleButton key={day} value={index} aria-label={day}>
                  {day}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            {!form.activeWeekdays?.length && (
              <Typography color="error" variant="caption" display="block">
                Select at least one active delivery day.
              </Typography>
            )}
          </Grid>
        </Grid>
      </Stack>
    </Paper>
  );
}

function BoxHeader() {
  return (
    <div>
      <Typography variant="h6">Delivery schedule</Typography>
      <Typography color="text.secondary">
        The same delivery window applies on each selected day.
      </Typography>
    </div>
  );
}
